import { Component, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { assetClasses, subClassOptionsByClass } from '@/pages/deals/data-access/deals.option';
import { KeyedOption } from '@/shared/enums/generic.enum';
import { HelpersService } from '@/shared/services/helpers.service';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { finalize } from 'rxjs';
import { CommonModule } from '@angular/common';
import { RequestDealAmountRange, RequestDealFormData, RequestDealTiming } from '@/shared/ui-components/data-access/request-deal-dialog.model';
import { AssetClass } from '@/pages/deals/data-access/deals.enum';
import { DialogComponent } from '../dialog/dialog.component';
import { ButtonComponent } from '../button/button.component';
import { InputComponent } from '../input/input.component';


@Component({
    selector: 'app-request-deal-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        SelectModule,
        InputTextModule,
        RadioButtonModule,
        DialogComponent,
        ButtonComponent,
        InputComponent
    ],
    templateUrl: './request-deal-dialog.component.html'
})
export class RequestDealDialogComponent implements OnInit {
    @Input() visible = false;
    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() onSubmitSuccess = new EventEmitter<void>();

    // Initialize form immediately to prevent template binding errors
    public requestDealForm: FormGroup = inject(FormBuilder).group({
        name: [''],
        assetClass: [null],
        assetSubClass: [null],
        investmentAmountRange: [null],
        targetInvestmentTime: [null],
        otherComments: ['']
    });
    public assetClasses: KeyedOption<AssetClass>[] = assetClasses;
    public filteredAssetSubClasses: KeyedOption<number>[] = [];
    public isSubmitting = signal<boolean>(false);
    public showDealCriteriaError = false;
    public readonly investmentAmountRangeOptions: { label: string; value: RequestDealAmountRange; averageAmount: number }[] = [
        { label: '$50K - $200K', value: '50K_200K', averageAmount: 125000 },
        { label: '$200K - $500K', value: '200K_500K', averageAmount: 350000 },
        { label: '$500K+', value: '500K_PLUS', averageAmount: 500000 }
    ];
    public readonly investmentTimingOptions: { label: string; value: RequestDealTiming }[] = [
        { label: 'Immediate / ASAP', value: 'ASAP' },
        { label: 'Future Investment Interest', value: 'FUTURE' }
    ];
    private readonly _fb = inject(FormBuilder);
    private readonly _http = inject(HttpClient);
    private readonly _helpersService = inject(HelpersService);
    private readonly _translateService = inject(TranslateService);
    private readonly apiUrl = environment.urls.api;

    public ngOnInit(): void {
        this.initializeForm();
    }

    public closeDialog(): void {
        this.visible = false;
        this.visibleChange.emit(false);
        this.requestDealForm.reset();
        this.filteredAssetSubClasses = [];
        this.showDealCriteriaError = false;
    }

    public submitRequest(): void {
        if (this.requestDealForm.invalid) {
            this.requestDealForm.markAllAsTouched();
            return;
        }

        this.isSubmitting.set(true);

        const formValue = this.requestDealForm.value as RequestDealFormData;
        const dealName = (formValue.name ?? '').trim();
        const hasDealName = dealName.length > 0;
        const hasAssetClass = formValue.assetClass != null;

        this.showDealCriteriaError = !hasDealName && !hasAssetClass;
        if (this.showDealCriteriaError) {
            this.requestDealForm.get('name')?.markAsTouched();
            this.requestDealForm.get('assetClass')?.markAsTouched();
            this.isSubmitting.set(false);
            return;
        }

        const selectedAmountRange = this.investmentAmountRangeOptions.find((option) => option.value === formValue.investmentAmountRange);
        const resolvedComment = (formValue.otherComments ?? '').trim();

        const timingValueByOption: Record<RequestDealTiming, string> = {
            ASAP: 'asap',
            FUTURE: 'futureInvestmentInterest'
        };

        const requestBody = {
            name: hasDealName ? dealName : null,
            assetClass: hasAssetClass ? formValue.assetClass : null,
            assetSubClass: hasAssetClass ? formValue.assetSubClass : null,
            investmentAmount: selectedAmountRange?.averageAmount ?? null,
            targetInvestmentTime: formValue.targetInvestmentTime ? timingValueByOption[formValue.targetInvestmentTime] : null,
            otherComments: resolvedComment || null
        };

        this._http
            .post(`${this.apiUrl}/ClientDeals/add-specific-deal-request`, requestBody)
            .pipe(finalize(() => this.isSubmitting.set(false)))
            .subscribe({
                next: () => {
                    this._helpersService.showSuccessMessage('Request submitted successfully');
                    this.onSubmitSuccess.emit();
                    this.closeDialog();
                },
                error: (err) => {
                    this._helpersService.showErrorMessage(err);
                }
            });
    }

    private initializeForm(): void {
        // Update existing form instead of recreating it
        this.requestDealForm.patchValue({
            name: '',
            assetClass: null,
            assetSubClass: null,
            investmentAmountRange: null,
            targetInvestmentTime: null,
            otherComments: ''
        });
        
        // Set validators
        this.requestDealForm.get('name')?.clearValidators();
        this.requestDealForm.get('assetClass')?.clearValidators();
        this.requestDealForm.get('assetSubClass')?.clearValidators();
        this.requestDealForm.get('investmentAmountRange')?.setValidators(Validators.required);
        this.requestDealForm.get('targetInvestmentTime')?.setValidators(Validators.required);
        this.requestDealForm.get('otherComments')?.clearValidators();
        this.requestDealForm.updateValueAndValidity({ emitEvent: false });

        // Subscribe to assetClass changes (only if not already subscribed)
        this.requestDealForm.get('assetClass')?.valueChanges.subscribe((assetClassId: number | null) => {
            this.showDealCriteriaError = false;
            const assetSubClassControl = this.requestDealForm.get('assetSubClass');
            if (!assetSubClassControl) return;
            if (assetClassId !== null) {
                this.filteredAssetSubClasses = subClassOptionsByClass[assetClassId as number] ?? [];
                assetSubClassControl.setValue(null);
                assetSubClassControl.setValidators(Validators.required);
            } else {
                this.filteredAssetSubClasses = [];
                assetSubClassControl.setValue(null);
                assetSubClassControl.clearValidators();
            }
            assetSubClassControl.updateValueAndValidity({ emitEvent: false });
        });
    }
}
